/**
 * Permission Service
 * Handles ACM (Access Control Management) permission checks
 */

import { MODULES, getModuleByRoute } from '../config/moduleConfig';

/**
 * Get current user from localStorage
 * @returns {object|null} User object or null
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

/**
 * Check if current user is Super Admin
 * @returns {boolean}
 */
export const isSuperAdmin = () => {
  const user = getCurrentUser();
  return user?.isSuperAdmin === true;
};

/**
 * Check if current user is a team member (non-super admin)
 * @returns {boolean}
 */
export const isTeamMember = () => {
  const user = getCurrentUser();
  return user?.isTeamMember === true;
};

/**
 * Get user's allowed module permissions
 * @returns {string[]} Array of module IDs
 */
export const getUserModulePermissions = () => {
  const user = getCurrentUser();

  // Super Admin has access to all modules
  if (user?.isSuperAdmin) {
    return Object.keys(MODULES);
  }

  return user?.modulePermissions || [];
};

/**
 * Check if user has access to a specific module
 * @param {string} moduleId - Module ID to check (e.g., 'ROOM', 'FOOD', 'TEAM')
 * @returns {boolean}
 */
export const hasModuleAccess = (moduleId) => {
  const user = getCurrentUser();

  // Not logged in
  if (!user) return false;

  // Not an admin
  if (user.role !== 1) return false;

  // Super Admin has access to all modules
  if (user.isSuperAdmin) return true;

  // Check if module exists
  const module = MODULES[moduleId];
  if (!module) return false;

  // Check if module is always accessible (e.g., DASHBOARD, NOTIFICATION)
  if (module.alwaysAccessible) return true;

  // Check if module is super admin only
  if (module.superAdminOnly) return false;

  // Check user's module permissions
  const permissions = user.modulePermissions || [];
  return permissions.includes(moduleId);
};

/**
 * Check if user has access to any of the specified modules
 * @param {string[]} moduleIds - Array of module IDs
 * @returns {boolean}
 */
export const hasAnyModuleAccess = (moduleIds) => {
  return moduleIds.some((moduleId) => hasModuleAccess(moduleId));
};

/**
 * Check if user can access a specific route
 * @param {string} path - Route path to check
 * @returns {boolean}
 */
export const canAccessRoute = (path) => {
  const user = getCurrentUser();

  // Not logged in
  if (!user) return false;

  // Not an admin
  if (user.role !== 1) return false;

  // Super Admin can access all routes
  if (user.isSuperAdmin) return true;

  // Find which module this route belongs to
  const module = getModuleByRoute(path);

  // Route not associated with any module - allow access
  if (!module) return true;

  // Check if module is always accessible (e.g., DASHBOARD, NOTIFICATION)
  if (module.alwaysAccessible) return true;

  // Check if module is super admin only
  if (module.superAdminOnly) return false;

  // Check module access
  return hasModuleAccess(module.id);
};

/**
 * Get all modules the user has access to
 * @returns {object[]} Array of module objects
 */
export const getAllowedModules = () => {
  const user = getCurrentUser();

  if (!user || user.role !== 1) return [];

  // Super Admin gets all modules
  if (user.isSuperAdmin) {
    return Object.values(MODULES);
  }

  // Filter modules based on permissions
  const permissions = user.modulePermissions || [];
  return Object.values(MODULES).filter((module) => {
    // Exclude super admin only modules
    if (module.superAdminOnly) return false;
    return permissions.includes(module.id);
  });
};

/**
 * Get sidebar menu items based on user permissions
 * @returns {object[]} Array of menu items
 */
export const getPermittedMenuItems = () => {
  return getAllowedModules().map((module) => ({
    id: module.id,
    name: module.name,
    icon: module.icon,
    path: module.path,
  }));
};

/**
 * Get the first allowed route for the user
 * Used for redirecting when user accesses unauthorized pages or as default landing page
 * @returns {string} First allowed route path or '/login' if no access
 */
export const getFirstAllowedRoute = () => {
  const user = getCurrentUser();

  // Not logged in
  if (!user) return '/login';

  // Super Admin - dashboard is first
  if (user.isSuperAdmin) return '/dashboard';

  // Get user's permissions
  const permissions = user.modulePermissions || [];

  // Priority order for landing pages
  const priorityModules = ['DASHBOARD', 'FOOD', 'TABLE', 'ROOM', 'USERS', 'AMENITY', 'ATTRACTION', 'COUPON', 'BANNER', 'ANALYTICS', 'TRANSPORTATION'];

  // Find first allowed module in priority order
  for (const moduleId of priorityModules) {
    if (permissions.includes(moduleId)) {
      const module = MODULES[moduleId];
      if (module && module.path) {
        return module.path;
      }
    }
  }

  // If none of priority modules, get first permission
  if (permissions.length > 0) {
    const firstModule = MODULES[permissions[0]];
    if (firstModule && firstModule.path) {
      return firstModule.path;
    }
  }

  // Fallback - should not happen if user has any permissions
  return '/login';
};

const PermissionService = {
  getCurrentUser,
  isSuperAdmin,
  isTeamMember,
  getUserModulePermissions,
  hasModuleAccess,
  hasAnyModuleAccess,
  canAccessRoute,
  getAllowedModules,
  getPermittedMenuItems,
};

export default PermissionService;
